import { useEffect, useState, useCallback, useRef } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, getDoc } from 'firebase/firestore'
import { TeamsInterface, Permissions, ProfileInterface,ResourceType,AccessType } from 'ts-interfaces';
import { syncBoardWriteAccess, syncProjectWriteAccessToBoards } from './updateBoardPermissions';
import { syncCollectionWriteAccess, syncProjectWriteAccessToCollections } from './updateCollectionPermissions';

const teamsCache = new Map<string, TeamsInterface[]>();

export const useTeams = (
    designFirmID: string | undefined, 
    db: any | null,
    includePermissions: boolean = true,
    includeProfile: boolean = true
) => {
    const [teams, setTeams] = useState<TeamsInterface[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const isFetchingRef = useRef(false);
  
    const fetchTeams = useCallback(async () => {
        if (!designFirmID || !db) {
            setLoading(false);
            return; 
        }
        
        if (isFetchingRef.current) return;
        
        isFetchingRef.current = true;
        setLoading(true);
        setError(null);
        
        try {
            // Check cache first
            const cacheKey = `${designFirmID}-${includePermissions}-${includeProfile}`;
            if (teamsCache.has(cacheKey)) {
                const cachedTeams = teamsCache.get(cacheKey);
                if (cachedTeams) {
                    setTeams(cachedTeams);
                    setLoading(false);
                    isFetchingRef.current = false;
                    console.log('Cache hit!');
                    return;
                }
            }
            
            // Fetch teams
            const teamsRef = collection(db, 'teams');
            const q = query(
                teamsRef, 
                where('designFirmID', '==', designFirmID), 
                where('status', '==', 'active')
            );
            const querySnapshot = await getDocs(q);
            
            if (querySnapshot.empty) {
                setTeams(null);
                setLoading(false);
                isFetchingRef.current = false;
                return;
            }

            // Get team data and extract UIDs (limit to 30 for 'in' query)
            const teamsData = querySnapshot.docs
                .slice(0, 30)
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as TeamsInterface));
            
            const uids = teamsData
                .map(team => team.uid)
                .filter((uid): uid is string => !!uid);

            // Batch fetch permissions and profiles if needed
            const enrichmentPromises: Promise<void>[] = [];
            const permissionsMap = new Map<string, Permissions>();
            const profilesMap = new Map<string, ProfileInterface>();

            if (uids.length > 0) {
                if (includePermissions) {
                    enrichmentPromises.push(
                        getDocs(query(collection(db, 'permissions'), where('__name__', 'in', uids)))
                            .then(snapshot => {
                                snapshot.docs.forEach(doc => {
                                    permissionsMap.set(doc.id, {
                                        id: doc.id,
                                        ...doc.data()
                                    } as Permissions);
                                });
                            })
                            .catch(error => console.error('Failed to fetch permissions:', error))
                    );
                }

                if (includeProfile) {
                    enrichmentPromises.push(
                        getDocs(query(collection(db, 'profiles'), where('uid', 'in', uids)))
                            .then(snapshot => {
                                //get length
                                snapshot.docs.forEach(doc => {
                                    const data = doc.data();
                                    profilesMap.set(data.uid, {
                                        id: doc.id,
                                        ...data
                                    } as ProfileInterface);
                                });
                            })
                            .catch(error => console.error('Failed to fetch profiles:', error))
                    );
                }

                await Promise.all(enrichmentPromises);
            }
            // Enrich team data with permissions and profiles
            const enrichedTeams = teamsData.map(team => {
                const enrichedTeam = { ...team } as any;
                
                if (team.uid) {
                    if (includePermissions) {
                        enrichedTeam.permissions = permissionsMap.get(team.uid);
                    }
                    if (includeProfile) {
                        enrichedTeam.profile = profilesMap.get(team.uid);
                    }
                }
                
                return enrichedTeam as TeamsInterface;
            });

            teamsCache.set(cacheKey, enrichedTeams);
            setTeams(enrichedTeams);
            
        } catch (e) {
            console.error('Error fetching teams:', e);
            setError(e instanceof Error ? e : new Error('Failed to fetch teams'));
            setTeams(null);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [designFirmID, db, includePermissions, includeProfile]);

    const updateTeamMember = useCallback(async (teamMemberUid: string, resourceId: string, resourceType: ResourceType,
        access: AccessType, deletePermission: boolean = false
    ) => {
        if (!db || !teamMemberUid || !resourceId) {
            throw new Error('Missing required parameters for updateTeamMember');
        }

        try {
            // Get current permissions
            const permissionsRef = doc(db, 'permissions', teamMemberUid);
            const permissionsDoc = await getDoc(permissionsRef);
            
            let currentPermissions: Partial<Permissions> = {};
            if (permissionsDoc.exists()) {
                currentPermissions = permissionsDoc.data() as Permissions;
            }
    
            // Build updated permissions
            const updatedPermissions = {
                ...currentPermissions,
                collectionRead: currentPermissions.collectionRead || [],
                collectionWrite: currentPermissions.collectionWrite || [],
                boardRead: currentPermissions.boardRead || [],
                boardWrite: currentPermissions.boardWrite || [],
                projectRead: currentPermissions.projectRead || [],
                projectWrite: currentPermissions.projectWrite || []
            };

            // Track project write changes for syncing
            const oldProjectWrite = [...updatedPermissions.projectWrite];
            let newProjectWrite = [...updatedPermissions.projectWrite];

            // Track board write changes for syncing
            const oldBoardWrite = [...updatedPermissions.boardWrite];
            let newBoardWrite = [...updatedPermissions.boardWrite];

            // Track collection write changes for syncing
            const oldCollectionWrite = [...updatedPermissions.collectionWrite];
            let newCollectionWrite = [...updatedPermissions.collectionWrite];
        
            // Get the arrays for the resource type
            const getArrays = () => {
                switch (resourceType) {
                    case ResourceType.Collections:
                        return {
                            read: [...updatedPermissions.collectionRead],
                            write: [...updatedPermissions.collectionWrite]
                        };
                    case ResourceType.Boards:
                        return {
                            read: [...updatedPermissions.boardRead],
                            write: [...updatedPermissions.boardWrite]
                        };
                    case ResourceType.Projects:
                        return {
                            read: [...updatedPermissions.projectRead],
                            write: [...updatedPermissions.projectWrite]
                        };
                }
            };

            const arrays = getArrays();

            // Update arrays based on access type or delete
            if (deletePermission) {
                // Remove from both read and write arrays
                arrays.read = arrays.read.filter(id => id !== resourceId);
                arrays.write = arrays.write.filter(id => id !== resourceId);
            } else if (access === 'read') {
                if (!arrays.read.includes(resourceId)) {
                    arrays.read.push(resourceId);
                }
                arrays.write = arrays.write.filter(id => id !== resourceId);
            } else if (access === 'write') {
                // if (!arrays.read.includes(resourceId)) {
                //     arrays.read.push(resourceId);
                // }
                if (!arrays.write.includes(resourceId)) {
                    arrays.write.push(resourceId);
                }
            }

            // Apply updates back to permissions
            switch (resourceType) {
                case ResourceType.Collections:
                    updatedPermissions.collectionRead = arrays.read;
                    updatedPermissions.collectionWrite = arrays.write;
                    newCollectionWrite = arrays.write;
                    break;
                case ResourceType.Boards:
                    updatedPermissions.boardRead = arrays.read;
                    updatedPermissions.boardWrite = arrays.write;
                    newBoardWrite = arrays.write;
                    break;
                case ResourceType.Projects:
                    updatedPermissions.projectRead = arrays.read;
                    updatedPermissions.projectWrite = arrays.write;
                    newProjectWrite = arrays.write;
                    break;
            }

            // Update in Firestore
            await updateDoc(permissionsRef, updatedPermissions);

            // Sync access changes in parallel
            const syncPromises = [];
            
            // If boardWrite was updated, sync the board documents
            const isBoardWriteUpdate = resourceType === ResourceType.Boards;
            if (isBoardWriteUpdate) {
                syncPromises.push(
                    syncBoardWriteAccess(db, oldBoardWrite, newBoardWrite, teamMemberUid)
                        .catch(syncError => {
                            console.error('Error syncing board write access:', syncError);
                        })
                );
            }

            // If collectionWrite was updated, sync collection-level access
            const isCollectionWriteUpdate = resourceType === ResourceType.Collections;
            if (isCollectionWriteUpdate) {
                syncPromises.push(
                    syncCollectionWriteAccess(db, oldCollectionWrite, newCollectionWrite, teamMemberUid)
                        .catch(syncError => { 
                            console.error('Error syncing collection write access:', syncError);
                            // Don't throw here - log the error but continue
                        })
                );
            }
            
            // If projectWrite was updated, sync project-level access to boards
            const isProjectWriteUpdate = resourceType === ResourceType.Projects;
            if (isProjectWriteUpdate) {
                syncPromises.push(
                    syncProjectWriteAccessToBoards(db, oldProjectWrite, newProjectWrite, teamMemberUid)
                        .catch(syncError => {
                            console.error('Error syncing project write access to boards:', syncError);
                            // Don't throw here - log the error but continue
                        }),
                    syncProjectWriteAccessToCollections(db, oldProjectWrite, newProjectWrite, teamMemberUid)
                        .catch(syncError => {
                            console.error('Error syncing project write access to collections:', syncError);
                            // Don't throw here - log the error but continue
                        })
                );

            }
            
            // Wait for all sync operations to complete
            if (syncPromises.length > 0) {
                await Promise.all(syncPromises);
            }

            // Update local state and cache
            const updateTeamInList = (teamsList: TeamsInterface[] | null) => {
                if (!teamsList) return null;
                
                return teamsList.map(team => {
                    if (team.uid === teamMemberUid) {
                        return {
                            ...team,
                            permissions: {
                                ...((team as any).permissions || {}),
                                ...updatedPermissions,
                                id: teamMemberUid
                            } as Permissions
                        } as any;
                    }
                    return team;
                });
            };

            setTeams(prevTeams => updateTeamInList(prevTeams));

            // Update all cache entries
            teamsCache.forEach((cachedTeams, cacheKey) => {
                const updated = updateTeamInList(cachedTeams);
                if (updated) {
                    teamsCache.set(cacheKey, updated);
                }
            });
            
        } catch (e) {
            console.error('Error updating team member permissions:', e);
            throw e instanceof Error ? e : new Error('Failed to update team member permissions');
        }
    }, [db]);

    useEffect(() => {
        fetchTeams();
    }, [fetchTeams]);
    return { 
        teams, 
        loading, 
        error,
        updateTeamMember
    };
}